import { getHourlyFloatRoleByKey } from "./hourlyFloatRoles";

/** Bookings with Standard/Premium hourly package rows (Hourly tab float) */
export const hasHourlyPackages = (booking) =>
    Array.isArray(booking?.hourlyPackages) && booking.hourlyPackages.length > 0;

const normalizePlanTier = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "");

export const isEditingFloatBooking = (booking) => {
    if (!booking) return false;
    const eventType = String(
        booking.eventType || booking.shootType || ""
    ).toLowerCase();
    const serviceName = String(
        booking.service_id?.serviceName || ""
    ).toLowerCase();

    return (
        booking.serviceCategory === "editing" ||
        eventType.includes("editing") ||
        serviceName.includes("editing")
    );
};

/** Requirement field only for Hourly Standard / Premium personalize bookings */
export const shouldShowFloatRequirement = (booking) =>
    isHourlyStandardOrPremiumBooking(booking);

/**
 * Which staff list to show in Float Booking:
 * - Editing bookings → editors only
 * - Hourly Standard/Premium → role from quote (photo / video / edit / etc.)
 * - All other bookings → photographers only
 */
export const getFloatStaffRole = (booking) => {
    if (isEditingFloatBooking(booking)) {
        const def = getHourlyFloatRoleByKey("editor");
        return {
            ...def,
            roleKey: "editor",
            displayTitle: def.title,
        };
    }

    if (
        isHourlyStandardOrPremiumBooking(booking) ||
        isCustomizeHourlyBooking(booking)
    ) {
        return getCustomizeHourlyRole(booking);
    }

    const def = getHourlyFloatRoleByKey("photographer");
    return {
        ...def,
        roleKey: "photographer",
        displayTitle: "Photographers",
    };
};

/**
 * Hourly Standard or Premium plan tier (for requirement + multi-role accordion).
 */
export const isHourlyStandardOrPremiumBooking = (booking) => {
    if (!booking) return false;

    const subTier = normalizePlanTier(
        booking.subCategory ||
            booking.subCategoryType ||
            booking.subCategoryName ||
            booking.sub_category ||
            ""
    );
    if (subTier === "standard" || subTier === "premium") {
        return true;
    }

    const hourlyPackages = booking.hourlyPackages || [];
    if (hourlyPackages.length > 0) {
        return hourlyPackages.some((pkg) => {
            const cat = normalizePlanTier(
                pkg.category || pkg.planCategory || pkg.subCategoryType || ""
            );
            return cat === "standard" || cat === "premium";
        });
    }

    const editingPackages = booking.editingPackages || [];
    if (editingPackages.length > 0) {
        return editingPackages.some((pkg) => {
            const cat = normalizePlanTier(
                pkg.category ||
                    pkg.planCategory ||
                    pkg.type ||
                    pkg.subCategoryType ||
                    ""
            );
            return cat === "standard" || cat === "premium";
        });
    }

    return false;
};

/**
 * Customize-tab hourly personalize bookings: use role_accordian accordion,
 * not the Hourly tab package-wise float UI.
 */
export const isCustomizeHourlyBooking = (booking) => {
    if (!booking) return false;

    if (isEditingFloatBooking(booking)) {
        return false;
    }

    // Hourly tab rows (package-wise float) use HourlyFloatBookingModal
    if (
        booking.serviceCategory === "hourly" &&
        hasHourlyPackages(booking)
    ) {
        return false;
    }


    const quoteType = String(booking.quoteType || "").toLowerCase();
    const eventType = String(
        booking.eventType || booking.shootType || ""
    ).toLowerCase();
    const subType = String(
        booking.subCategoryType || booking.subCategoryName || ""
    ).toLowerCase();

    const fromPersonalizedQuote =
        quoteType === "personalizedquotes" ||
        quoteType === "hourly quote" ||
        Boolean(booking.quoteId);

    if (fromPersonalizedQuote) {
        if (
            eventType.includes("hourly") ||
            subType === "standard" ||
            subType === "premium" ||
            subType === "shoot_team"
        ) {
            return true;
        }
    }

    return false;
};

/** Single role accordion config from quote role_accordian */
export const getCustomizeHourlyRole = (booking) => {
    const keys = resolveRoleKeysFromAccordion(booking);
    const roleKey = keys?.[0] || "photographer";
    const def = getHourlyFloatRoleByKey(roleKey);
    const displayTitle =
        String(booking?.role_accordian || "").trim() || def.title;

    return { ...def, roleKey, displayTitle };
};

export const resolveBookingFloatBaseAmount = (booking) => {
    const total = parseFloat(
        booking?.finalAmount ||
            booking?.bookingAmount ||
            booking?.totalAmount ||
            0
    );
    return Math.round(Number.isNaN(total) ? 0 : total);
};
/**
 * Map quote role_accordian / requirements text to hourly float role keys.
 * Returns null when no filter → show every role required by package services.
 */
export const resolveRoleKeysFromAccordion = (booking) => {
    const raw =
        booking?.role_accordian ||
        (Array.isArray(booking?.requirements)
            ? booking.requirements.join(" ")
            : booking?.requirements) ||
        booking?.requirement ||
        "";

    const source = raw.toString().trim().toLowerCase();
    if (!source) return null;

    const keys = new Set();
    if (source.includes("video")) keys.add("videographer");
    if (source.includes("edit")) keys.add("editor");
    if (source.includes("light") || source.includes("drone")) keys.add("lightingSetup");
    if (source.includes("photo")) keys.add("photographer");

    if (keys.size === 0) keys.add("photographer");
    return Array.from(keys);
};

export const roleKeyMatchesPackageServices = (roleKey, currentPkg) => {
    if (!currentPkg) return false;

    const matchingServices = (currentPkg.services || []).filter((s) => {
        const name = (s.name || "").toLowerCase();
        const key = roleKey.toLowerCase();
        if (key === "lightingsetup") return name.includes("lighting");
        if (key === "editor") return name.includes("edit");
        return name.includes(key);
    });

    const totalRequired = matchingServices.reduce((sum, s) => sum + (s.qty || 0), 0);
    return totalRequired > 0;
};
